export default {
  privacy: {
    title: 'Política de privacidad',
    lastUpdated: 'Última actualización:',
    lastUpdatedDate: '16 de octubre de 2026',
    backToHome: '← Volver al inicio',
    seoDescription:
      'Conozca cómo Geofleet.ai recopila, usa y protege su información, incluidas las integraciones de Google Workspace y Google Sheets, las limitaciones de IA/ML y sus derechos de privacidad.',
    intro: [
      'Geofleet.ai ("Geofleet", "nosotros", "nos" o "nuestro") respeta su privacidad y se compromete a proteger la información que se nos confía.',
      'Esta Política de privacidad explica cómo recopilamos, usamos, divulgamos, retenemos y protegemos la información cuando accede o utiliza el sitio web de Geofleet.ai, las aplicaciones web, las aplicaciones móviles, las API, las integraciones y los servicios relacionados (colectivamente, los "Servicios").',
      'Al utilizar los Servicios, usted reconoce las prácticas descritas en esta Política de privacidad.',
    ],
    sections: {
      informationWeCollect: {
        title: '1. Información que recopilamos',
        intro: 'Según cómo utilice los Servicios, podemos recopilar las siguientes categorías de información.',
        account: {
          title: '1.1 Información de cuenta y perfil',
          intro: 'Esto puede incluir:',
          items: [
            'Nombre',
            'Dirección de correo electrónico',
            'Número de teléfono',
            'Nombre de la empresa u organización',
            'Cargo',
            'Credenciales de cuenta e información de autenticación',
            'Preferencias del usuario',
            'Otra información proporcionada voluntariamente durante el registro o la administración de la cuenta',
          ],
        },
        delivery: {
          title: '1.2 Información de entrega y operativa',
          intro: 'Según las funciones utilizadas, podemos procesar:',
          items: [
            'Información de entrega y pedidos',
            'Información de clientes y destinatarios',
            'Direcciones de entrega',
            'Información de conductores',
            'Información de vehículos',
            'Ubicaciones de conductores y vehículos',
            'Rutas y trayectos',
            'Estado de la entrega',
            'Prueba de entrega',
            'Firmas electrónicas',
            'OTP e información de verificación',
            'Información del odómetro',
            'Información de geocercas',
            'Notas y registros operativos',
            'Otra información enviada por clientes o usuarios a través de los Servicios',
          ],
        },
        device: {
          title: '1.3 Información del dispositivo y técnica',
          intro: 'Podemos recopilar automáticamente:',
          items: [
            'Dirección IP',
            'Tipo de navegador',
            'Sistema operativo',
            'Tipo de dispositivo',
            'Identificadores del dispositivo',
            'Información de registro',
            'Información de la aplicación',
            'Ubicación aproximada derivada de información técnica cuando corresponda',
            'Información de uso e interacción',
          ],
        },
        payment: {
          title: '1.4 Información de pago y facturación',
          intro: 'Cuando corresponda, podemos procesar:',
          items: [
            'Información de facturación',
            'Información de suscripción',
            'Información de transacciones',
            'Estado del pago',
          ],
          note:
            'La información de la tarjeta de pago puede ser procesada directamente por proveedores de pago externos. No necesariamente almacenamos la información completa de la tarjeta de pago en nuestros sistemas.',
        },
        communications: {
          title: '1.5 Comunicaciones',
          intro: 'Podemos recopilar información contenida en:',
          items: [
            'Solicitudes de soporte',
            'Correos electrónicos',
            'Comentarios',
            'Comunicaciones del servicio',
            'Otras comunicaciones con Geofleet',
          ],
        },
      },
      googleWorkspace: {
        title: '2. Datos de Google Workspace y Google Sheets',
        intro: [
          'Geofleet ofrece una integración opcional con Google Workspace que permite a los usuarios autorizados importar información desde Google Sheets a Geofleet.',
          'Cuando un usuario conecta una cuenta de Google a Geofleet, accedemos únicamente a la información de Google Workspace necesaria para proporcionar la funcionalidad de importación de Google Sheets solicitada por el usuario.',
          'La integración utiliza los siguientes permisos de la API de Google:',
        ],
        drive: {
          title: 'Metadatos de Google Drive — drive.metadata.readonly',
          paragraphs: [
            'Geofleet utiliza acceso de solo lectura a metadatos de Google Drive para descubrir y enumerar archivos disponibles en el Google Drive del usuario, incluidos Google Sheets.',
            'Esto permite a Geofleet identificar información como:',
          ],
          items: [
            'Nombre de la hoja de cálculo',
            'ID del archivo',
            'Tipo de archivo',
            'Otros metadatos de archivo relevantes necesarios para identificar la hoja de cálculo',
          ],
          closing: [
            'Este permiso se usa para permitir que el usuario seleccione la Google Sheet que desea usar.',
            'Geofleet no usa este permiso para crear, modificar o eliminar archivos en el Google Drive del usuario.',
          ],
        },
        sheets: {
          title: 'Google Sheets de solo lectura — spreadsheets.readonly',
          paragraphs: [
            'Después de que el usuario selecciona una hoja de cálculo, Geofleet usa acceso de solo lectura para leer el contenido de la Google Sheet seleccionada y la hoja/pestaña elegida.',
            'La información se carga en la interfaz de importación de Geofleet para que el usuario pueda:',
          ],
          numbered: [
            'Revisar la información de la hoja de cálculo.',
            'Seleccionar la hoja/pestaña adecuada.',
            'Asignar columnas de la hoja de cálculo a campos de Geofleet.',
            'Completar la importación solicitada.',
          ],
          closing: ['Geofleet no modifica ni elimina los datos de Google Sheets del usuario.'],
        },
      },
      googleProcessing: {
        title: '3. Procesamiento y almacenamiento de datos de Google Workspace',
        paragraphs: [
          'La información de Google Workspace a la que se accede a través de la integración se procesa únicamente para proporcionar la funcionalidad de importación de Google Sheets solicitada por el usuario y la funcionalidad relacionada de Geofleet.',
          'La hoja de cálculo de Google de origen y su contenido no se retienen como una copia separada en el backend de Geofleet como parte del proceso de importación de Google Sheets.',
          'Cuando un usuario importa intencionalmente información a Geofleet, la información resultante puede formar parte de la cuenta de Geofleet del usuario y almacenarse como datos operativos de Geofleet.',
          'Por ejemplo, si un usuario importa registros de conductores desde una Google Sheet, la información de conductores resultante puede almacenarse en Geofleet para que el usuario pueda usar y gestionar esos registros a través de los Servicios.',
          'Dicha información importada se trata de conformidad con esta Política de privacidad y los requisitos contractuales y de retención de datos aplicables.',
          'Geofleet no usa datos de Google Workspace para publicidad, corretaje de datos ni venta a terceros.',
        ],
      },
      googleLimitedUse: {
        title: '4. Uso limitado de los servicios de API de Google',
        paragraphs: [
          'El uso por parte de Geofleet de la información recibida de las API de Google cumple con la Google API Services User Data Policy, incluidos los requisitos de Limited Use.',
          'Geofleet no vende datos de usuarios de Google.',
          'Los datos de Google Workspace obtenidos a través de las API de Google no se usan para crear, entrenar o mejorar modelos generalizados o fundamentales de inteligencia artificial o aprendizaje automático.',
          'Geofleet no transfiere datos de Google Workspace a servicios de inteligencia artificial o aprendizaje automático de terceros con el fin de entrenar o mejorar sus modelos.',
          'Los datos de Google Workspace se usan únicamente para la funcionalidad orientada al usuario descrita en esta Política de privacidad y para fines permitidos según las políticas aplicables de los servicios de API de Google.',
        ],
      },
      howWeUse: {
        title: '5. Cómo usamos la información',
        intro: 'Podemos usar la información recopilada a través de los Servicios para:',
        items: [
          'Proporcionar y operar los Servicios.',
          'Crear y gestionar cuentas de usuario.',
          'Procesar pedidos, entregas, rutas, trayectos y flujos de trabajo operativos.',
          'Proporcionar integraciones y funcionalidades solicitadas.',
          'Procesar importaciones iniciadas por el usuario.',
          'Proporcionar atención al cliente.',
          'Comunicarnos con los usuarios sobre los Servicios.',
          'Procesar suscripciones y pagos.',
          'Mantener, asegurar y solucionar problemas de los Servicios.',
          'Detectar y prevenir fraude, abuso y acceso no autorizado.',
          'Supervisar el rendimiento y la fiabilidad del sistema.',
          'Analizar el uso de los Servicios.',
          'Mejorar la funcionalidad y la experiencia de usuario de los Servicios.',
          'Cumplir obligaciones legales aplicables.',
          'Hacer cumplir nuestros Términos de servicio y otros acuerdos aplicables.',
        ],
        note:
          'La información obtenida a través de las API de Google Workspace se usa únicamente para fines permitidos según la Google API Services User Data Policy aplicable y los requisitos de Limited Use.',
      },
      aiMl: {
        title: '6. Inteligencia artificial y aprendizaje automático',
        paragraphs: [
          'Geofleet puede proporcionar funcionalidad impulsada por IA, automatización, analítica, recomendaciones o apoyo a la decisión como parte de los Servicios.',
          'Cuando usa un asistente de IA en los Servicios, como el Copilot de GeoFleet Lite, guardamos sus mensajes y las respuestas del asistente con la cuenta de su organización. Los usamos para mostrar su historial de conversaciones en cualquier dispositivo y para mejorar y entrenar las funciones de IA de Geofleet. Nunca se incluyen datos de Google Workspace. Su organización puede pedirnos que eliminemos este historial contactando con soporte.',
          'Los datos de Google Workspace obtenidos a través de las API de Google no se usan para entrenar, ajustar, crear o mejorar modelos generalizados o fundamentales de IA/ML.',
          'Los datos de Google Workspace tampoco se transfieren a proveedores de IA/ML de terceros con el fin de entrenar o mejorar sus modelos.',
          'Cualquier funcionalidad de IA o automatización que involucre información obtenida a través de las API de Google Workspace está sujeta a las limitaciones y requisitos descritos en esta Política de privacidad y a las políticas aplicables de Google.',
        ],
      },
      sharing: {
        title: '7. Compartición y divulgación',
        intro: 'Podemos divulgar información en las siguientes circunstancias.',
        serviceProviders: {
          title: 'Proveedores de servicios',
          paragraphs: [
            'Podemos usar proveedores de servicios externos de confianza para ayudarnos a alojar, operar, asegurar, mantener y proporcionar los Servicios.',
            'Estos proveedores pueden procesar información en nuestro nombre y se espera que usen dicha información solo para los servicios que proporcionan a Geofleet y sujetos a obligaciones contractuales y de seguridad adecuadas.',
          ],
        },
        legal: {
          title: 'Requisitos legales',
          intro: 'Podemos divulgar información cuando sea razonablemente necesario para:',
          items: [
            'Cumplir la ley aplicable o un proceso legal.',
            'Responder a solicitudes legítimas de autoridades gubernamentales.',
            'Proteger los derechos, la propiedad o la seguridad de Geofleet, de nuestros usuarios o de terceros.',
            'Detectar, prevenir o investigar fraude o incidentes de seguridad.',
          ],
        },
        business: {
          title: 'Transacciones comerciales',
          paragraphs: [
            'La información puede transferirse como parte de una fusión, adquisición, financiación, reestructuración, venta de activos o transacción corporativa similar, sujeta a los requisitos legales aplicables.',
          ],
        },
        authorization: {
          title: 'Con su autorización',
          paragraphs: [
            'Podemos divulgar información cuando usted nos haya autorizado o instruido específicamente a hacerlo.',
            'No vendemos información personal con fines de marketing de terceros.',
            'No proporcionamos datos de Google Workspace a terceros para publicidad, venta, corretaje de datos o entrenamiento de modelos generalizados o fundamentales de IA/ML.',
          ],
        },
      },
      googleAuth: {
        title: '8. Autorización y revocación de Google',
        paragraphs: [
          'Cuando conecta su cuenta de Google a Geofleet, autoriza a Geofleet a acceder a la información de Google Workspace descrita durante el proceso de autorización de Google.',
          'Puede revocar la autorización de Google de Geofleet en cualquier momento a través de la configuración de su cuenta de Google o de la funcionalidad disponible de Geofleet.',
          'Tras revocar la autorización, Geofleet ya no podrá acceder a la información de Google Workspace mediante la autorización revocada.',
          'Revocar la autorización de Google no elimina automáticamente la información que usted importó previamente e intencionalmente a Geofleet.',
          'Los datos de Geofleet importados previamente siguen sujetos a los procedimientos aplicables de retención y eliminación de Geofleet.',
        ],
      },
      retention: {
        title: '9. Retención de datos',
        intro: 'Retenemos la información durante el tiempo razonablemente necesario para:',
        items: [
          'Proporcionar los Servicios.',
          'Mantener las cuentas de clientes.',
          'Cumplir obligaciones contractuales.',
          'Proporcionar atención al cliente.',
          'Cumplir requisitos legales, contables o regulatorios.',
          'Resolver disputas.',
          'Prevenir fraude y abuso.',
          'Hacer cumplir nuestros acuerdos.',
        ],
        paragraphs: [
          'La hoja de cálculo de Google de origen y su contenido no se retienen como una copia separada en nuestro backend como parte del proceso de importación de Google Sheets.',
          'La información importada intencionalmente a Geofleet puede retenerse según los requisitos aplicables de cuenta, suscripción, operativos, legales y contractuales.',
          'Cuando la información ya no sea razonablemente necesaria, podemos eliminarla o anonimizarla de conformidad con nuestros procedimientos de retención y la ley aplicable.',
        ],
      },
      security: {
        title: '10. Seguridad',
        paragraphs: [
          'Implementamos medidas técnicas y organizativas razonables diseñadas para proteger la información contra el acceso, uso, alteración, divulgación o destrucción no autorizados.',
          'Según el servicio y el entorno aplicables, las salvaguardas pueden incluir:',
        ],
        items: [
          'Cifrado en tránsito.',
          'Cifrado en reposo cuando corresponda.',
          'Controles de acceso.',
          'Mecanismos de autenticación.',
          'Permisos basados en roles.',
          'Monitoreo y registro.',
          'Controles de seguridad de la infraestructura.',
          'Mecanismos de copia de seguridad y recuperación.',
          'Evaluaciones de seguridad y controles operativos.',
        ],
        note: 'Ningún método de transmisión, procesamiento o almacenamiento puede garantizarse como completamente seguro.',
      },
      rights: {
        title: '11. Sus derechos y opciones',
        intro:
          'Según la ley aplicable y su ubicación, usted puede tener derechos relacionados con su información personal, incluido el derecho a:',
        items: [
          'Acceder a la información que tenemos sobre usted.',
          'Solicitar la corrección de información inexacta.',
          'Solicitar la eliminación de información.',
          'Solicitar la restricción de cierto procesamiento.',
          'Oponerse a cierto procesamiento.',
          'Solicitar la portabilidad de cierta información.',
          'Retirar el consentimiento cuando el procesamiento se base en el consentimiento.',
          'Presentar una queja ante una autoridad de protección de datos pertinente.',
        ],
        contactIntro: 'Para ejercer los derechos aplicables o enviar una solicitud de privacidad, contáctenos en:',
        verify: 'Es posible que debamos verificar su identidad antes de cumplir determinadas solicitudes.',
      },
      cookies: {
        title: '12. Cookies y tecnologías similares',
        intro: 'Nosotros y nuestros proveedores de servicios podemos usar cookies, balizas web, píxeles, almacenamiento local y tecnologías similares para:',
        items: [
          'Operar los Servicios.',
          'Recordar preferencias.',
          'Mantener la autenticación.',
          'Comprender el uso.',
          'Mejorar el rendimiento.',
          'Detectar problemas de seguridad.',
          'Analizar el tráfico del sitio web.',
        ],
        note:
          'Puede controlar las cookies a través de la configuración de su navegador. Desactivar ciertas cookies puede afectar la disponibilidad o funcionalidad de partes de los Servicios.',
      },
      international: {
        title: '13. Transferencias internacionales de datos',
        paragraphs: [
          'Geofleet y sus proveedores de servicios pueden procesar información en países distintos del país en el que usted reside.',
          'Cuando corresponda, tomamos medidas razonables para garantizar que las transferencias internacionales estén sujetas a las salvaguardas adecuadas exigidas por la ley aplicable.',
        ],
      },
      children: {
        title: '14. Privacidad de los menores',
        paragraphs: [
          'Los Servicios están destinados a usuarios empresariales y no están dirigidos a personas menores de 18 años.',
          'No recopilamos a sabiendas información personal directamente de menores de 18 años.',
          'Si cree que un menor nos ha proporcionado información personal, contáctenos en:',
        ],
        closing:
          'Tomaremos medidas razonables para investigar y eliminar dicha información cuando corresponda.',
      },
      thirdParty: {
        title: '15. Servicios de terceros',
        paragraphs: [
          'Los Servicios pueden contener enlaces a o integraciones con sitios web, aplicaciones, API y servicios de terceros.',
          'Los servicios de terceros se rigen por sus propios términos y políticas de privacidad.',
          'Geofleet no es responsable de las prácticas de privacidad de servicios de terceros que no estén controlados por Geofleet.',
          'Cuando habilite una integración, debe revisar la política de privacidad y los términos del proveedor tercero aplicable.',
        ],
      },
      changes: {
        title: '16. Cambios a esta Política de privacidad',
        paragraphs: [
          'Podemos actualizar periódicamente esta Política de privacidad para reflejar cambios en nuestros Servicios, tecnología, requisitos legales o prácticas comerciales.',
          'Cuando realicemos cambios, actualizaremos la fecha de "Última actualización" en la parte superior de esta Política de privacidad.',
          'Cuando corresponda, los cambios materiales también pueden comunicarse por correo electrónico, a través de los Servicios u otro método razonable.',
          'Su uso continuado de los Servicios después de que una Política de privacidad actualizada entre en vigor constituye el reconocimiento de la política actualizada en la medida permitida por la ley aplicable.',
        ],
      },
      contact: {
        title: '17. Contáctenos',
        intro:
          'Si tiene preguntas, inquietudes o solicitudes relacionadas con esta Política de privacidad o nuestras prácticas de privacidad, contáctenos:',
        company: 'Geofleet.ai',
        emailLabel: 'Correo electrónico:',
        websiteLabel: 'Sitio web:',
        website: 'https://geofleet.ai',
      },
    },
  },
};
